import React from 'react';
import { Box, Typography } from '@mui/material';
import { SessionTypeChip } from '@motivo/guanyin/src/components/SessionTypeChip';
import { ResultOf } from ':src/graphql';
import { userDetailsByIdQuery } from '../../graphql/queries';
import { Section } from '../Section';
import ZeusSessionLink from '../shared/ZeusSessionLink';

type User = NonNullable<ResultOf<typeof userDetailsByIdQuery>['userById']>;

interface UpcomingSessionsSectionProps {
  user: User;
  sessions: NonNullable<ResultOf<typeof userDetailsByIdQuery>['sessions']>['result'];
}

const MAX_ATTENDEES_TO_SHOW = 2;

function getOtherPartyNames({
  user,
  session,
}: {
  user: User;
  session: NonNullable<ResultOf<typeof userDetailsByIdQuery>['sessions']>['result'][number];
}) {
  if (user.id !== session.createdBy) {
    return session.supervisor.fullName;
  }

  const allNames = session.sessionAttendees.map((attendee) => attendee.user.fullName);
  const displayNames = allNames.slice(0, MAX_ATTENDEES_TO_SHOW);
  const remainingCount = allNames.length - MAX_ATTENDEES_TO_SHOW;
  const superviseeNames = `${displayNames.join(', ')}${
    remainingCount > 0 ? ` + ${remainingCount} more` : ''
  }`;
  return superviseeNames;
}

export default function UpcomingSessionsSection({ user, sessions }: UpcomingSessionsSectionProps) {
  if (sessions.length === 0) {
    return null;
  }

  return (
    <Section
      title="Upcoming sessions"
      count={sessions.length}
      summaryLink={{
        label: 'View all sessions in Zeus →',
        path: `/users/${user.id}/sessions`,
      }}
    >
      {sessions.map((session) => {
        const otherPartyNames = getOtherPartyNames({ user, session });

        return (
          <Box key={session.id} mx={-3} px={3} py={2} borderBottom={1} borderColor="divider">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <ZeusSessionLink session={session} />
              <SessionTypeChip
                size="small"
                sessionTypeId={session.sessionTypeId}
                label={session.sessionType.displayName}
              />
            </Box>
            <Typography>{otherPartyNames}</Typography>
          </Box>
        );
      })}
    </Section>
  );
}
